// src/auth/AuthComponent.jsx
import React, { useState } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  sendEmailVerification,
  updateProfile // ✅ FIXED: Import updateProfile correctly
} from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../config/firebase-config';
import { Eye, EyeOff, Mail, Lock, User, CheckCircle, AlertCircle } from 'lucide-react';
import { RequestASongLogo } from '../components/common/RequestASongLogo.jsx';

const AuthComponent = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: ''
  });
  const [isLogin, setIsLogin] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);

  // Password strength checker
  const getPasswordStrength = (password) => {
    if (!password) return { score: 0, label: '', color: '' };
    
    let score = 0;
    if (password.length >= 8) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[a-z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;

    const levels = {
      0: { label: '', color: '' },
      1: { label: 'Very Weak', color: '#EF4444' },
      2: { label: 'Weak', color: '#F97316' },
      3: { label: 'Good', color: '#EAB308' },
      4: { label: 'Strong', color: '#22C55E' },
      5: { label: 'Very Strong', color: '#15803D' }
    };

    return { score, ...levels[score] };
  };

  const passwordStrength = getPasswordStrength(formData.password);

  const validateForm = () => {
    const errors = [];

    // Email validation
    if (!formData.email) {
      errors.push('Email is required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.push('Please enter a valid email address');
    }

    if (!isLogin) {
      // Signup validation
      if (!formData.password) {
        errors.push('Password is required');
      } else if (formData.password.length < 8) {
        errors.push('Password must be at least 8 characters');
      }

      if (!formData.confirmPassword) {
        errors.push('Please confirm your password');
      } else if (formData.password !== formData.confirmPassword) {
        errors.push('Passwords do not match');
      }
    } else {
      // Login validation
      if (!formData.password) {
        errors.push('Password is required');
      }
    }

    return errors;
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors[0]);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      if (isLogin) {
        // Sign in existing user
        await signInWithEmailAndPassword(auth, formData.email, formData.password);
        console.log('✅ User signed in successfully');
      } else {
        // Create new user (OPTIMIZED - NO PHONE REQUIRED)
        const userCredential = await createUserWithEmailAndPassword(
          auth, 
          formData.email, 
          formData.password
        );
        
        console.log('✅ User created:', userCredential.user.uid);
        
        // ✅ FIXED: Use updateProfile correctly
        if (formData.name.trim()) {
          await updateProfile(userCredential.user, {
            displayName: formData.name.trim()
          });
          console.log('✅ Profile updated with display name');
        }
        
        // IMMEDIATELY send clean email verification (no earnings text)
        try {
          const functions = getFunctions();
          const sendEmailVerification = httpsCallable(functions, 'sendEmailVerification');
          const result = await sendEmailVerification();
          
          if (!result.data.success) {
            throw new Error(result.data.message);
          }

          if (result.data.success) {
            setEmailVerificationSent(true);
            console.log('📧 Clean email verification sent to:', formData.email);
          } else {
            throw new Error(result.data.message);
          }
        } catch (verificationError) {
          console.error('⚠️ Custom verification failed, using Firebase default:', verificationError);
          // Fallback to Firebase default if our custom function fails
          await sendEmailVerification(userCredential.user);
          setEmailVerificationSent(true);
        }
      }
    } catch (error) {
      console.error('❌ Auth error:', error);
      
      // User-friendly error messages
      switch (error.code) {
        case 'auth/email-already-in-use':
          setError('This email is already registered. Try signing in instead.');
          break;
        case 'auth/weak-password':
          setError('Password is too weak. Please choose a stronger password.');
          break;
        case 'auth/user-not-found':
          setError('No account found with this email. Try creating an account.');
          break;
        case 'auth/wrong-password':
          setError('Incorrect password. Please try again.');
          break;
        case 'auth/invalid-email':
          setError('Please enter a valid email address.');
          break;
        case 'auth/too-many-requests':
          setError('Too many failed attempts. Please try again later.');
          break;
        default:
          setError('Authentication failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '24px',
        padding: '48px',
        maxWidth: '480px',
        width: '100%',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        border: '8px solid #7C3AED'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ marginBottom: '24px' }}>
            <RequestASongLogo size={80} showText={true} />
          </div>
          <h1 style={{
            fontSize: '32px',
            fontWeight: 'bold',
            margin: '0 0 8px 0',
            background: 'linear-gradient(135deg, #7C3AED, #A78BFA)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            {isLogin ? 'Welcome Back' : 'Join RequestASong'}
          </h1>
          <p style={{
            color: '#6B7280',
            fontSize: '18px',
            margin: '0'
          }}>
            {isLogin 
              ? 'Sign in to your musician account'
              : 'Start earning money from your music in minutes! 🚀'
            }
          </p>
        </div>

        {/* Benefits for Signup */}
        {!isLogin && (
          <div style={{
            backgroundColor: '#F3F4F6',
            borderRadius: '16px',
            padding: '20px',
            marginBottom: '32px',
            border: '2px solid #E5E7EB'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '16px',
              fontSize: '14px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CheckCircle size={16} style={{ color: '#10B981' }} />
                <span>No setup fees</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CheckCircle size={16} style={{ color: '#10B981' }} />
                <span>Instant SMS alerts</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CheckCircle size={16} style={{ color: '#10B981' }} />
                <span>Set your own prices</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CheckCircle size={16} style={{ color: '#10B981' }} />
                <span>Quick 3-min setup</span>
              </div>
            </div>
          </div>
        )}

        {/* Email Verification Success */}
        {emailVerificationSent && (
          <div style={{
            backgroundColor: '#ECFDF5',
            border: '2px solid #10B981',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <CheckCircle size={20} style={{ color: '#10B981' }} />
            <div>
              <p style={{ margin: 0, color: '#065F46', fontWeight: '600' }}>
                Account created! Check your email to verify your account.
              </p>
              <p style={{ margin: '4px 0 0 0', color: '#047857', fontSize: '14px' }}>
                We sent a verification link to {formData.email}
              </p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div style={{
            backgroundColor: '#FEF2F2',
            border: '2px solid #FECACA',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <AlertCircle size={20} style={{ color: '#DC2626' }} />
            <p style={{ margin: 0, color: '#DC2626', fontWeight: '500' }}>
              {error}
            </p>
          </div>
        )}

        <form onSubmit={handleAuth}>
          {/* Email Field */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              fontSize: '16px',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '8px'
            }}>
              Email Address *
            </label>
            <div style={{ position: 'relative' }}>
              <Mail 
                size={20} 
                style={{
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9CA3AF'
                }}
              />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="your.email@example.com"
                style={{
                  width: '100%',
                  padding: '16px 16px 16px 48px',
                  border: `2px solid ${error && error.includes('email') ? '#EF4444' : '#D1D5DB'}`,
                  borderRadius: '12px',
                  fontSize: '16px',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7C3AED'}
                onBlur={(e) => e.target.style.borderColor = error && error.includes('email') ? '#EF4444' : '#D1D5DB'}
              />
            </div>
          </div>

          {/* Name Field (Only for Signup) */}
          {!isLogin && (
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '16px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Name <span style={{ color: '#9CA3AF', fontWeight: 'normal' }}>(optional)</span>
              </label>
              <div style={{ position: 'relative' }}>
                <User 
                  size={20} 
                  style={{
                    position: 'absolute',
                    left: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#9CA3AF'
                  }}
                />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Your name"
                  style={{
                    width: '100%',
                    padding: '16px 16px 16px 48px',
                    border: '2px solid #D1D5DB',
                    borderRadius: '12px',
                    fontSize: '16px',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#7C3AED'}
                  onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                />
              </div>
            </div>
          )}

          {/* Password Field */}
          <div style={{ marginBottom: isLogin ? '32px' : '24px' }}>
            <label style={{
              display: 'block',
              fontSize: '16px',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '8px'
            }}>
              Password *
            </label>
            <div style={{ position: 'relative' }}>
              <Lock 
                size={20} 
                style={{
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9CA3AF'
                }}
              />
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                placeholder={isLogin ? "Enter your password" : "Create a strong password"}
                style={{
                  width: '100%',
                  padding: '16px 48px 16px 48px',
                  border: `2px solid ${error && error.includes('password') ? '#EF4444' : '#D1D5DB'}`,
                  borderRadius: '12px',
                  fontSize: '16px',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7C3AED'}
                onBlur={(e) => e.target.style.borderColor = error && error.includes('password') ? '#EF4444' : '#D1D5DB'}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#9CA3AF',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            
            {/* Password Strength Indicator (Signup only) */}
            {!isLogin && formData.password && (
              <div style={{ marginTop: '8px' }}>
                <div style={{
                  display: 'flex',
                  gap: '4px',
                  marginBottom: '4px'
                }}>
                  {[1, 2, 3, 4, 5].map(level => (
                    <div
                      key={level}
                      style={{
                        height: '4px',
                        flex: 1,
                        borderRadius: '2px',
                        backgroundColor: level <= passwordStrength.score 
                          ? passwordStrength.color 
                          : '#E5E7EB'
                      }}
                    />
                  ))}
                </div>
                {passwordStrength.label && (
                  <span style={{
                    fontSize: '12px',
                    color: passwordStrength.color,
                    fontWeight: '500'
                  }}>
                    {passwordStrength.label}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Confirm Password Field (Signup only) */}
          {!isLogin && (
            <div style={{ marginBottom: '32px' }}>
              <label style={{
                display: 'block',
                fontSize: '16px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Confirm Password *
              </label>
              <div style={{ position: 'relative' }}>
                <Lock 
                  size={20} 
                  style={{
                    position: 'absolute',
                    left: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#9CA3AF'
                  }}
                />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  placeholder="Confirm your password"
                  style={{
                    width: '100%',
                    padding: '16px 48px 16px 48px',
                    border: `2px solid ${error && error.includes('match') ? '#EF4444' : '#D1D5DB'}`,
                    borderRadius: '12px',
                    fontSize: '16px',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#7C3AED'}
                  onBlur={(e) => e.target.style.borderColor = error && error.includes('match') ? '#EF4444' : '#D1D5DB'}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: 'absolute',
                    right: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: '#9CA3AF',
                    cursor: 'pointer',
                    padding: '4px'
                  }}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: '100%',
              backgroundColor: isLoading ? '#9CA3AF' : '#7C3AED',
              color: 'white',
              padding: '16px',
              borderRadius: '12px',
              border: 'none',
              fontSize: '18px',
              fontWeight: '600',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              opacity: isLoading ? 0.7 : 1,
              marginBottom: '24px'
            }}
            onMouseOver={(e) => {
              if (!isLoading) e.target.style.backgroundColor = '#6D28D9';
            }}
            onMouseOut={(e) => {
              if (!isLoading) e.target.style.backgroundColor = '#7C3AED';
            }}
          >
            {isLoading ? (
              isLogin ? 'Signing In...' : 'Creating Account...'
            ) : (
              isLogin ? 'Sign In' : 'Create Account - It\'s Free! 🚀'
            )}
          </button>

          {/* Toggle Login/Signup */}
          <div style={{ textAlign: 'center' }}>
            <span style={{ color: '#6B7280' }}>
              {isLogin ? "Don't have an account? " : "Already have an account? "}
            </span>
            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
                setEmailVerificationSent(false);
                setFormData(prev => ({ ...prev, confirmPassword: '', name: '' }));
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#7C3AED',
                fontWeight: '600',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              {isLogin ? 'Sign up' : 'Sign in'}
            </button>
          </div>
        </form>

        {/* Legal Links */}
        <div style={{
          marginTop: '24px',
          padding: '16px',
          backgroundColor: '#F9FAFB',
          borderRadius: '8px',
          fontSize: '12px',
          color: '#6B7280',
          textAlign: 'center',
          lineHeight: '1.4'
        }}>
          🔒 {isLogin ? 'Secure login' : 'Your email will be verified before account activation'}. We'll never share your information. 
          <br />
          By {isLogin ? 'signing in' : 'signing up'}, you agree to our{' '}
          <a href="/terms" style={{ color: '#7C3AED', textDecoration: 'underline' }}>Terms of Service</a>
          {' '}and{' '}
          <a href="/privacy" style={{ color: '#7C3AED', textDecoration: 'underline' }}>Privacy Policy</a>.
        </div>

        {/* Admin Link */}
        <div style={{
          marginTop: '16px',
          textAlign: 'center'
        }}>
          <a
            href="/admin"
            style={{
              color: '#6B7280',
              fontSize: '12px',
              textDecoration: 'underline'
            }}
          >
            Platform Administrator? Access Admin Dashboard →
          </a>
        </div>
      </div>
    </div>
  );
};

export default AuthComponent;